<script lang="ts" setup>
import { computed } from "vue";
import ShowMoreList from "@/components/lists/ShowMoreList.vue";
import TravelOrderStatusLabel from "@/views/trips/components/TravelOrderStatus.vue";
import { useFilters } from "@/composables/useFilters";
import { useOrder } from "@/composables/useOrder";
import { TravelOrderCancellationStatusMap } from "@/constants/trip";
import type { TravelOrderDto } from "@/api/trips/dto/trip.dto";

const { round } = useFilters();
const { getOrderRoute } = useOrder();

const props = defineProps<{
  travelOrder: TravelOrderDto;
}>();

const isCancelled = computed<boolean>(() => {
  return TravelOrderCancellationStatusMap.some((s) => s.id === props.travelOrder.status);
});

const rate = computed<number>(() => {
  return round(props.travelOrder.rate ?? 0);
});

const orderRoute = computed<string>(() => {
  return getOrderRoute(props.travelOrder.order);
});
</script>

<template>
  <div class="d-flex justify-space-between">
    <div class="d-flex align-center">
      <div class="text-primary font-size-12 font-weight-500" data-qa="route-travel-order-number">
        TO: #{{ props.travelOrder.id }}
      </div>
      <travel-order-status-label
        v-if="isCancelled"
        :travel-order="props.travelOrder"
        class="ml-2"
        data-qa="route-travel-order-status"
      ></travel-order-status-label>
    </div>
    <div class="d-flex align-center">
      <div class="text-grey-darken-1 font-size-12 d-flex">
        <span class="mr-1">REF:</span>
        <show-more-list
          :items="props.travelOrder.order.refNumbers"
          data-qa="route-ref-number"
          item-class="text-grey-darken-1 font-size-12"
        ></show-more-list>
      </div>
      <div class="text-grey-darken-2 font-size-12 font-weight-500 ml-4" data-qa="route-rate">{{ rate }}$</div>
      <router-link
        v-if="props.travelOrder.order.id"
        :to="{ name: 'order-view', params: { id: props.travelOrder.order.id } }"
        class="text-primary font-size-12 font-weight-500 ml-4 text-decoration-none"
        data-qa="route-order-number"
        target="_blank"
      >
        Order: #{{ props.travelOrder.order.number }}
      </router-link>
      <v-tooltip location="top">
        <template #activator="{ props: tooltipProps }">
          <v-icon
            v-if="props.travelOrder.order.temperatures?.length || props.travelOrder.order.protectFromFreezing"
            class="ml-2"
            color="info"
            data-qa="route-temperature-control"
            icon="mdi-snowflake"
            size="16"
            v-bind="tooltipProps"
          ></v-icon>
        </template>
        <template v-if="props.travelOrder.order.protectFromFreezing">Protect from freezing</template>
        <template v-else-if="props.travelOrder.order.temperatures?.length">
          Temperature control from {{ props.travelOrder.order.temperatures[0] }} to
          {{ props.travelOrder.order.temperatures[1] }} ℉
        </template>
      </v-tooltip>
    </div>
  </div>
  <div class="mt-3 justify-space-between">
    <div class="font-weight-500 text-grey-darken-3 mb-2" data-qa="route-stop-road">
      {{ orderRoute }}
    </div>
    <router-link
      :to="{ name: 'customer-edit', params: { id: props.travelOrder.order.customer.id } }"
      class="font-size-12 text-uppercase font-weight-500 text-primary text-decoration-none"
      data-qa="route-customer-name"
      target="_blank"
    >
      {{ props.travelOrder.order.customer.name }}
    </router-link>
  </div>
</template>

<style lang="scss" scoped></style>
